import { useRef, useState } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { Loader2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDateToYmd } from '@/features/auth/utils/formatDate';
import {
  buildValidationRules,
  fieldKey,
  isSelectType,
  optionLabel,
  optionValue,
} from '@/features/auth/utils/onboardingFields';
import { KYC } from '@/features/auth/types/onboardingFields';
import type {
  FormValues,
  NormalizedField,
} from '@/features/auth/types/onboardingFields';
import type { UseMutationResult } from '@tanstack/react-query';
import {
  GST_MAX_LENGTH,
  PAN_MAX_LENGTH,
  sanitizeGstInput,
  sanitizePanInput,
} from '@/features/team/utils/kycInputMask';
import { useDeleteFile } from '@/shared/lib/upload/useDeleteFile';
import { getErrorMessage } from '@/shared/api/errors';

type UploadResponse = { data?: { mediaId?: string } };

type OnboardingFieldInputProps = {
  field: NormalizedField;
  register: UseFormRegister<FormValues>;
  control: Control<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  watch: UseFormWatch<FormValues>;
  uploadMutation: UseMutationResult<
    UploadResponse,
    Error,
    { file: File },
    unknown
  >;
  verifyPanPending: boolean;
  verifyGstPending: boolean;
  isFieldLocked: (name: string) => boolean;
  shouldShowVerifyButton: (name: string) => boolean;
  isKycField: (name: string) => boolean;
  onVerifyPan: (name: string) => void;
  onVerifyGst: (name: string) => void;
  onUnlockVerifiedField: (name: string) => void;
  forceOptional?: boolean;
};

const inputClass = (locked?: boolean, uppercase?: boolean) =>
  `signup-input box-border h-[44px] w-full min-w-0 rounded-[9px] border border-[#E2E9E7] px-3.5 text-sm leading-none text-[#0F1817] outline-none transition-[border-color,box-shadow] placeholder:text-[#A9B4B1] ${
    locked ? 'cursor-not-allowed bg-[#F2F5F4] text-[#7C8B88]' : 'bg-white'
  }${uppercase ? ' uppercase' : ''}`;

export function OnboardingFieldInput({
  field,
  register,
  control,
  setValue,
  watch,
  uploadMutation,
  verifyPanPending,
  verifyGstPending,
  isFieldLocked,
  shouldShowVerifyButton,
  isKycField,
  onVerifyPan,
  onVerifyGst,
  onUnlockVerifiedField,
  forceOptional = false,
}: OnboardingFieldInputProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const deleteMutation = useDeleteFile();
  const [uploadedMediaId, setUploadedMediaId] = useState(
    () => String(watch('mediaId') || ''),
  );
  const [uploadedFileName, setUploadedFileName] = useState('');
  const {
    fieldName,
    fieldLabel,
    fieldType,
    placeholder,
    validation,
    isRequired,
    options,
  } = field;

  const rules = buildValidationRules(
    fieldLabel,
    fieldType,
    validation,
    forceOptional ? false : isRequired,
  );
  const selectedFile = watch(fieldName) as FileList | string | undefined;
  const locked = isFieldLocked(fieldName);
  const key = fieldKey(fieldName);
  const isPan = key === KYC.PAN;
  const isGst = key === KYC.GST;
  const verifying = isPan ? verifyPanPending : verifyGstPending;
  const maxLen = validation?.maxLength ?? validation?.max;
  const kycMaxLen = isPan
    ? PAN_MAX_LENGTH
    : isGst
      ? GST_MAX_LENGTH
      : maxLen != null
        ? Number(maxLen)
        : undefined;

  const selectedFileName =
    uploadedFileName ||
    (typeof selectedFile === 'string'
      ? selectedFile
      : selectedFile?.[0]?.name || '');
  const mediaId = uploadedMediaId || String(watch('mediaId') || '');
  const hasUploadedFile = Boolean(selectedFileName || mediaId);
  const fileBusy = uploadMutation.isPending || deleteMutation.isPending;

  const clearFileField = () => {
    setUploadedMediaId('');
    setUploadedFileName('');
    setValue(fieldName, null, { shouldValidate: true, shouldDirty: true });
    setValue('mediaId', '', { shouldDirty: true });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteFile = async () => {
    if (fileBusy) return;
    const id = mediaId;
    if (!id) {
      clearFileField();
      return;
    }
    try {
      await deleteMutation.mutateAsync({ mediaId: id });
    } catch (error) {
      // Signup flow has SESSION_ID only — API may 401, but still clear local file.
      toast.error(getErrorMessage(error, 'Failed to delete file'));
    } finally {
      clearFileField();
    }
  };

  if (fieldType === 'file') {
    const {
      ref: registerRef,
      onChange: registerOnChange,
      ...fileRegister
    } = register(fieldName, rules);

    return (
      <div className="signup-input flex h-[44px] w-full items-center gap-2 rounded-[9px] border border-[#E2E9E7] bg-white px-3.5">
        <label className="flex min-w-0 flex-1 cursor-pointer items-center justify-between">
          <span className="truncate text-sm text-[#7C8B88]">
            {selectedFileName || placeholder || 'Choose file to upload'}
          </span>
          {!hasUploadedFile ? (
            <span className="shrink-0 text-[11px] font-semibold tracking-wide text-[#119C84] uppercase">
              Browse
            </span>
          ) : null}
          <input
            type="file"
            className="hidden"
            disabled={fileBusy}
            {...fileRegister}
            ref={(el) => {
              registerRef(el);
              fileInputRef.current = el;
            }}
            onChange={async (e) => {
              void registerOnChange(e);
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                if (mediaId) {
                  try {
                    await deleteMutation.mutateAsync({ mediaId });
                  } catch {
                    // best-effort replace
                  }
                }
                const response = await uploadMutation.mutateAsync({ file });
                const nextId = String(response?.data?.mediaId || '');
                if (!nextId) throw new Error('Upload did not return a media id');
                setUploadedMediaId(nextId);
                setUploadedFileName(file.name);
                setValue('mediaId', nextId, { shouldDirty: true });
              } catch (error) {
                toast.error(getErrorMessage(error, 'File upload failed'));
                clearFileField();
              }
            }}
          />
        </label>
        {hasUploadedFile ? (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void handleDeleteFile();
            }}
            disabled={fileBusy}
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#7C8B88] transition hover:bg-[#F2F5F4] hover:text-[#0F1817] disabled:opacity-50"
            aria-label="Delete uploaded file"
          >
            {fileBusy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <X className="h-3.5 w-3.5" />
            )}
          </button>
        ) : null}
      </div>
    );
  }

  if (fieldType === 'date') {
    const maxDOB = new Date();
    maxDOB.setFullYear(maxDOB.getFullYear() - 15);
    const minDOB = new Date();
    minDOB.setFullYear(minDOB.getFullYear() - 45);

    return (
      <Controller
        name={fieldName}
        control={control}
        rules={rules}
        render={({ field: formField }) => (
          <input
            type="date"
            className={inputClass()}
            min={formatDateToYmd(minDOB)}
            max={formatDateToYmd(maxDOB)}
            value={
              typeof formField.value === 'string'
                ? formField.value
                : formField.value
                  ? formatDateToYmd(formField.value as string)
                  : ''
            }
            onChange={(e) => formField.onChange(e.target.value || null)}
          />
        )}
      />
    );
  }

  if (isSelectType(fieldType, options)) {
    return (
      <select
        className={inputClass(locked)}
        disabled={locked}
        {...register(fieldName, rules)}
      >
        <option value="">
          {placeholder || `Select ${fieldLabel.toLowerCase()}`}
        </option>
        {options.map((opt, i) => (
          <option key={`${optionValue(opt)}-${i}`} value={optionValue(opt)}>
            {optionLabel(opt)}
          </option>
        ))}
      </select>
    );
  }

  if (isPan || isGst) {
    return (
      <div className="flex items-center gap-2">
        <Controller
          name={fieldName}
          control={control}
          rules={rules}
          render={({ field: formField }) => (
            <input
              type="text"
              inputMode="text"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              placeholder={placeholder}
              readOnly={locked}
              className={inputClass(locked, true)}
              maxLength={kycMaxLen}
              value={String(formField.value ?? '')}
              onBlur={formField.onBlur}
              onChange={(e) => {
                formField.onChange(
                  isPan
                    ? sanitizePanInput(e.target.value)
                    : sanitizeGstInput(e.target.value),
                );
              }}
            />
          )}
        />

        {shouldShowVerifyButton(fieldName) ? (
          <button
            type="button"
            onClick={() =>
              isPan ? onVerifyPan(fieldName) : onVerifyGst(fieldName)
            }
            disabled={verifying}
            className="signup-btn h-[44px] shrink-0 rounded-[9px] border-none px-4 text-[12px] font-semibold text-white disabled:opacity-50"
          >
            {verifying ? 'Verifying…' : 'Verify'}
          </button>
        ) : null}

        {locked && isKycField(fieldName) ? (
          <button
            type="button"
            onClick={() => onUnlockVerifiedField(fieldName)}
            className="h-[44px] shrink-0 rounded-[9px] border border-[#119C84] bg-white px-4 text-[12px] font-semibold text-[#119C84]"
          >
            Edit
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type={fieldType === 'number' ? 'text' : fieldType}
        placeholder={placeholder}
        readOnly={locked}
        className={inputClass(locked)}
        maxLength={maxLen != null ? Number(maxLen) : undefined}
        {...register(fieldName, rules)}
      />

      {shouldShowVerifyButton(fieldName) ? (
        <button
          type="button"
          onClick={() =>
            isPan ? onVerifyPan(fieldName) : onVerifyGst(fieldName)
          }
          disabled={verifying}
          className="signup-btn h-[44px] shrink-0 rounded-[9px] border-none px-4 text-[12px] font-semibold text-white disabled:opacity-50"
        >
          {verifying ? 'Verifying…' : 'Verify'}
        </button>
      ) : null}

      {locked && isKycField(fieldName) ? (
        <button
          type="button"
          onClick={() => onUnlockVerifiedField(fieldName)}
          className="h-[44px] shrink-0 rounded-[9px] border border-[#119C84] bg-white px-4 text-[12px] font-semibold text-[#119C84]"
        >
          Edit
        </button>
      ) : null}
    </div>
  );
}

export function getFieldErrorMessage(
  errors: FieldErrors<FormValues>,
  fieldName: string,
): string {
  const error = errors[fieldName];
  if (!error || typeof error !== 'object') return '';
  return String((error as { message?: string }).message || '');
}
